// ---------------------------------------------------------------------------
// sync-bus — BroadcastChannel-based multi-tab sync coordination
// ---------------------------------------------------------------------------
// Elects a single "leader" tab across all open tabs of the same origin.
// The leader drains the offline mutation queue; other tabs defer to it.
// Events are event-driven; no polling, no timeouts.
// ---------------------------------------------------------------------------

const CHANNEL_NAME = 'hotelstack-sync-bus';
const HEARTBEAT_INTERVAL_MS = 2500;
const LEADER_STALE_MS = 8000;

export type SyncBusRole = 'leader' | 'follower' | 'candidate';

export interface SyncBusMessage {
  type: 'LEADER_HEARTBEAT' | 'LEADER_CLAIM' | 'LEADER_RELEASE' | 'SYNC_STARTED' | 'SYNC_DONE' | 'REQUEST_LEADER' | 'SYNC_REQUEST';
  originId: string;
  timestamp: number;
  payload?: unknown;
}

type Listener = (message: SyncBusMessage) => void;

class SyncBus {
  private channel: BroadcastChannel | null = null;
  private originId: string;
  private role: SyncBusRole = 'candidate';
  private leaderId: string | null = null;
  private leaderLastSeen = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private listeners = new Set<Listener>();
  private started = false;

  constructor() {
    this.originId = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
      ? crypto.randomUUID()
      : `o-${Math.random().toString(36).slice(2)}-${Date.now()}`;
  }

  /** Start the sync bus. Safe to call multiple times. */
  start(): void {
    if (this.started) return;
    this.started = true;
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;

    try {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.addEventListener('message', this.handleMessage);
      this.channel.postMessage({
        type: 'REQUEST_LEADER',
        originId: this.originId,
        timestamp: Date.now(),
      } satisfies SyncBusMessage);
    } catch {
      // BroadcastChannel not available — fall through to single-tab behaviour
      this.role = 'leader';
      this.leaderId = this.originId;
    }

    // Heartbeat — also event-driven; uses setInterval only to detect a stale
    // leader (no per-message polling).
    this.heartbeatTimer = setInterval(() => {
      if (this.role === 'leader') {
        this.broadcast({ type: 'LEADER_HEARTBEAT', originId: this.originId, timestamp: Date.now() });
      } else if (this.leaderId && Date.now() - this.leaderLastSeen > LEADER_STALE_MS) {
        // Leader went stale — try to take over
        this.role = 'candidate';
        this.leaderId = null;
        this.broadcast({ type: 'REQUEST_LEADER', originId: this.originId, timestamp: Date.now() });
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.channel) {
      this.broadcast({ type: 'LEADER_RELEASE', originId: this.originId, timestamp: Date.now() });
      this.channel.removeEventListener('message', this.handleMessage);
      this.channel.close();
      this.channel = null;
    }
    this.listeners.clear();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  getRole(): SyncBusRole { return this.role; }
  getOriginId(): string { return this.originId; }
  isLeader(): boolean { return this.role === 'leader'; }

  /** Broadcast an event to all tabs. Used by the leader to notify followers. */
  notifySyncStarted(): void {
    this.broadcast({ type: 'SYNC_STARTED', originId: this.originId, timestamp: Date.now() });
  }

  notifySyncDone(payload?: unknown): void {
    this.broadcast({ type: 'SYNC_DONE', originId: this.originId, timestamp: Date.now(), payload });
  }

  requestSync(): void {
    this.broadcast({ type: 'SYNC_REQUEST', originId: this.originId, timestamp: Date.now() });
  }

  private broadcast(message: SyncBusMessage): void {
    try {
      this.channel?.postMessage(message);
    } catch {
      // channel closed during teardown — safe to ignore
    }
  }

  private handleMessage = (event: MessageEvent<SyncBusMessage>) => {
    const msg = event.data;
    if (!msg || msg.originId === this.originId) return;

    switch (msg.type) {
      case 'LEADER_HEARTBEAT':
        if (this.leaderId === msg.originId) {
          this.leaderLastSeen = Date.now();
        } else if (this.leaderId === null) {
          this.leaderId = msg.originId;
          this.leaderLastSeen = Date.now();
          this.role = 'follower';
        }
        break;
      case 'LEADER_CLAIM':
        this.leaderId = msg.originId;
        this.leaderLastSeen = Date.now();
        this.role = 'follower';
        break;
      case 'LEADER_RELEASE':
        if (this.leaderId === msg.originId) {
          this.leaderId = null;
          this.leaderLastSeen = 0;
          this.role = 'candidate';
        }
        break;
      case 'REQUEST_LEADER':
        if (this.role === 'leader') {
          // Announce ourselves so the requester becomes a follower
          this.broadcast({ type: 'LEADER_CLAIM', originId: this.originId, timestamp: Date.now() });
        }
        break;
      case 'SYNC_REQUEST':
        if (this.role === 'leader') {
          this.notifySyncStarted();
        }
        break;
      default:
        break;
    }

    this.listeners.forEach((l) => {
      try {
        l(msg);
      } catch {
        // listener errors must not break the bus
      }
    });
  };
}

let singleton: SyncBus | null = null;

export function getSyncBus(): SyncBus {
  if (singleton === null) {
    singleton = new SyncBus();
  }
  return singleton;
}

export type { SyncBus };